import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SharedModule } from '@/shared.module';
import { AnamneseService, FormService, NnnService, StorageService } from '@/services';
import type {
  NandaDiagnoseDetail,
  NandaDiagnoseKenmerk,
  NandaDiagnoseKenmerken,
  NandaDiagnoseSummary,
  NandaDomein,
  NandaKlasse,
} from '@/services';
import { Form } from '@/models';
import type { FormRegistration } from '@/models';
import type {
  AddNnnRegistrationRowDialogData,
  AddNnnRegistrationRowDialogResult,
  NnnKenmerkType,
} from '../../types/careplan-nnn.types';
import { buildKenmerkSecties } from '../../utils/build-kenmerk-secties';
import { includeSelectedKenmerken } from '../../utils/include-selected-kenmerken';
import { kenmerkKey } from '../../utils/kenmerk-key';
import { pickLatestFinalRegistration } from '../../utils/pick-latest-final-registration';
import { selectRowKenmerken } from '../../utils/select-row-kenmerken';

interface DiagnoseTreeKlasse {
  key: string;
  label: string;
  omschrijving: string;
  definitie: string;
  diagnoses: NandaDiagnoseSummary[];
}

interface DiagnoseTreeDomein {
  key: string;
  label: string;
  omschrijving: string;
  definitie: string;
  klassen: DiagnoseTreeKlasse[];
}

@Component({
  selector: 'app-add-nnn-registration-row-dialog',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './add-nnn-registration-row-dialog.component.html',
  styleUrl: './add-nnn-registration-row-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddNnnRegistrationRowDialogComponent implements OnInit {
  private readonly nnnService = inject(NnnService);
  private readonly anamneseService = inject(AnamneseService);
  private readonly formService = inject(FormService);
  private readonly storageService = inject(StorageService);
  private readonly dialogData = inject<AddNnnRegistrationRowDialogData>(MAT_DIALOG_DATA);

  protected readonly isEdit = !!this.dialogData.diagnose;

  protected readonly loading = signal(false);
  protected readonly diagnoses = signal<NandaDiagnoseSummary[]>([]);
  protected readonly domeinen = signal<NandaDomein[]>([]);
  protected readonly klassen = signal<NandaKlasse[]>([]);

  protected readonly searchText = signal('');
  private readonly expandedDomeinen = signal<ReadonlySet<string>>(new Set());
  private readonly expandedKlassen = signal<ReadonlySet<string>>(new Set());

  protected readonly selected = signal<NandaDiagnoseSummary | null>(this.dialogData.diagnose ?? null);
  protected readonly detail = signal<NandaDiagnoseDetail | null>(null);
  protected readonly kenmerken = signal<NandaDiagnoseKenmerken | null>(null);
  protected readonly kenmerkenLoading = signal(false);
  protected readonly kenmerkenLoadFailed = signal(false);
  private readonly preselectedKenmerken = this.dialogData.selectedKenmerken ?? [];
  private readonly preselectedKeys: ReadonlySet<string> = new Set(
    this.preselectedKenmerken.map(k => kenmerkKey(k.type, k.nnnKenmerkDbRef))
  );
  private readonly checkedKeys = signal<ReadonlySet<string>>(this.preselectedKeys);

  protected readonly kenmerkSecties = computed(() =>
    buildKenmerkSecties(this.kenmerken(), {
      selectableOnly: true,
      keepKeys: this.isEdit ? this.preselectedKeys : undefined,
    })
  );
  protected readonly hasSelectableKenmerken = computed(() =>
    this.kenmerkSecties().some(sectie => sectie.groepen.length > 0)
  );
  protected readonly canSubmit = computed(() => this.selected() !== null && this.kenmerken() !== null);
  protected readonly result = computed<AddNnnRegistrationRowDialogResult | null>(() => {
    const diagnose = this.selected();
    if (!diagnose) {
      return null;
    }
    return {
      diagnose,
      kenmerken: selectRowKenmerken(this.kenmerkSecties(), this.checkedKeys()),
      beschikbareKenmerken: this.kenmerken(),
    };
  });

  protected readonly emptyAnamnesisForm = new Form();
  protected readonly anamnesisOpen = signal(false);
  protected readonly anamnesisForms = signal<Form[]>([]);
  protected readonly selectedAnamnesisFormId = signal<string | null>(null);
  protected readonly anamnesisLoading = signal(false);
  protected readonly anamnesisRegistration = signal<FormRegistration | null>(null);

  protected readonly anamnesisToggleLabel = computed(() =>
    this.anamnesisOpen()
      ? 'epd.functions.careplan-nnn.add-row-dialog.hide-anamnesis'
      : 'epd.functions.careplan-nnn.add-row-dialog.view-anamnesis'
  );

  protected readonly selectedAnamnesisForm = computed(
    () => this.anamnesisForms().find(form => form.id === this.selectedAnamnesisFormId()) ?? this.emptyAnamnesisForm
  );

  private anamnesisFormsRequested = false;

  protected readonly searchActive = computed(() => this.searchText().trim().length > 0);

  protected readonly filteredDiagnoses = computed(() => {
    const query = this.searchText().trim().toLowerCase();
    if (!query) {
      return this.diagnoses();
    }
    return this.diagnoses().filter(
      d =>
        d.omschrijving.toLowerCase().includes(query) ||
        (d.focus ?? '').toLowerCase().includes(query) ||
        d.code.toString().includes(query)
    );
  });

  protected readonly tree = computed<DiagnoseTreeDomein[]>(() => {
    const diagnosesByKlasse = new Map<string, NandaDiagnoseSummary[]>();
    for (const diagnose of this.diagnoses()) {
      const key = `${diagnose.domein}:${diagnose.klasse}`;
      const list = diagnosesByKlasse.get(key);
      if (list) {
        list.push(diagnose);
      } else {
        diagnosesByKlasse.set(key, [diagnose]);
      }
    }
    const klassen = this.klassen();
    return this.domeinen().map(domein => ({
      key: String(domein.nummer),
      label: String(domein.nummer),
      omschrijving: domein.naam,
      definitie: domein.definitie,
      klassen: klassen
        .filter(klasse => klasse.domeinNummer === domein.nummer)
        .map(klasse => ({
          key: `${domein.nummer}:${klasse.nummer}`,
          label: String(klasse.nummer),
          omschrijving: klasse.naam,
          definitie: klasse.definitie,
          diagnoses: diagnosesByKlasse.get(`${domein.nummer}:${klasse.nummer}`) ?? [],
        })),
    }));
  });

  ngOnInit(): void {
    const preset = this.dialogData.diagnose;
    if (preset) {
      void this.loadDiagnoseContent(preset);
    } else {
      void this.loadReferenceData();
    }
  }

  private async loadReferenceData(): Promise<void> {
    this.loading.set(true);
    const [diagnoses, domeinen, klassen] = await Promise.all([
      this.nnnService.getNandaDiagnoses(undefined, true),
      this.nnnService.getNandaDomeinen(),
      this.nnnService.getNandaKlassen(),
    ]);
    this.loading.set(false);

    if (diagnoses.data) {
      this.diagnoses.set(diagnoses.data);
    }
    if (domeinen.data) {
      this.domeinen.set(domeinen.data);
    }
    if (klassen.data) {
      this.klassen.set(klassen.data);
    }
  }

  protected onSearchInput(event: Event): void {
    this.searchText.set((event.target as HTMLInputElement).value);
  }

  protected isDomeinExpanded(domeinKey: string): boolean {
    return this.expandedDomeinen().has(domeinKey);
  }

  protected isKlasseExpanded(klasseKey: string): boolean {
    return this.expandedKlassen().has(klasseKey);
  }

  protected setDomeinExpanded(domeinKey: string, expanded: boolean): void {
    this.expandedDomeinen.update(keys => toggleKey(keys, domeinKey, expanded));
  }

  protected setKlasseExpanded(klasseKey: string, expanded: boolean): void {
    this.expandedKlassen.update(keys => toggleKey(keys, klasseKey, expanded));
  }

  protected select(diagnose: NandaDiagnoseSummary): void {
    if (this.selected()?.code === diagnose.code) {
      return;
    }
    this.selected.set(diagnose);
    this.revealInTree(diagnose);
    this.checkedKeys.set(new Set());
    void this.loadDiagnoseContent(diagnose);
  }

  private async loadDiagnoseContent(diagnose: NandaDiagnoseSummary): Promise<void> {
    this.detail.set(null);
    this.kenmerken.set(null);
    this.kenmerkenLoadFailed.set(false);
    this.kenmerkenLoading.set(true);
    const [detail, kenmerken] = await Promise.all([
      this.nnnService.getNandaDiagnoseDetail(diagnose.code),
      this.nnnService.getNandaDiagnoseKenmerken(diagnose.code),
    ]);
    if (this.selected()?.code !== diagnose.code) {
      return;
    }
    this.kenmerkenLoading.set(false);
    if (detail.data) {
      this.detail.set(detail.data);
    }
    if (kenmerken.data) {
      this.kenmerken.set(
        this.isEdit ? includeSelectedKenmerken(kenmerken.data, this.preselectedKenmerken) : kenmerken.data
      );
    } else {
      this.kenmerkenLoadFailed.set(true);
    }
  }

  private revealInTree(diagnose: NandaDiagnoseSummary): void {
    if (diagnose.domein == null || diagnose.klasse == null) {
      return;
    }
    this.expandedDomeinen.update(keys => toggleKey(keys, String(diagnose.domein), true));
    this.expandedKlassen.update(keys => toggleKey(keys, `${diagnose.domein}:${diagnose.klasse}`, true));
  }

  protected isChecked(type: NnnKenmerkType, kenmerk: NandaDiagnoseKenmerk): boolean {
    return kenmerk.code !== null && this.checkedKeys().has(kenmerkKey(type, String(kenmerk.code)));
  }

  protected toggleKenmerk(type: NnnKenmerkType, kenmerk: NandaDiagnoseKenmerk): void {
    if (kenmerk.code === null) {
      return;
    }
    const key = kenmerkKey(type, String(kenmerk.code));
    this.checkedKeys.update(keys => toggleKey(keys, key, !keys.has(key)));
  }

  protected toggleAnamnesis(): void {
    const open = !this.anamnesisOpen();
    this.anamnesisOpen.set(open);
    if (open && !this.anamnesisFormsRequested) {
      this.anamnesisFormsRequested = true;
      void this.loadAnamnesisForms();
    }
  }

  private async loadAnamnesisForms(): Promise<void> {
    const careInstitutionId = this.storageService.getItem('activeCareInstitution');
    if (!careInstitutionId) {
      return;
    }
    this.anamnesisLoading.set(true);
    const { data } = await this.anamneseService.getAnamnesisSets(careInstitutionId, true, this.dialogData.patientId);
    this.anamnesisLoading.set(false);
    if (data?.forms) {
      this.anamnesisForms.set([...data.forms].sort((a, b) => a.order - b.order));
    }
  }

  protected async selectAnamnesisForm(formId: string): Promise<void> {
    this.selectedAnamnesisFormId.set(formId);
    this.anamnesisRegistration.set(null);
    this.anamnesisLoading.set(true);
    const { data: registrations } = await this.formService.getRegistrationsForPatient(
      formId,
      this.dialogData.patientId
    );
    if (this.selectedAnamnesisFormId() !== formId) {
      return;
    }
    const latestFinal = registrations ? pickLatestFinalRegistration(registrations) : null;
    if (!latestFinal?.id) {
      this.anamnesisLoading.set(false);
      return;
    }
    const { data: registration } = await this.formService.getRegistration(latestFinal.form.id, latestFinal.id);
    if (this.selectedAnamnesisFormId() !== formId) {
      return;
    }
    this.anamnesisLoading.set(false);
    if (registration) {
      this.anamnesisRegistration.set(registration);
    }
  }
}

function toggleKey(keys: ReadonlySet<string>, key: string, present: boolean): ReadonlySet<string> {
  const next = new Set(keys);
  if (present) {
    next.add(key);
  } else {
    next.delete(key);
  }
  return next;
}
